import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges
} from '@angular/core';
import OlMap from 'ol/Map';
import Feature from 'ol/Feature';
import Collection from 'ol/Collection';
import {Vector as VectorLayer} from 'ol/layer';
import {Translate} from 'ol/interaction';
import {transform} from 'ol/proj';
import {Subject} from 'rxjs';
import {MapFeatureUtils} from '../../../pages/client/monitor/map/map-feature.utils';
import {MapPosition} from '../../../pages/client/tasks/route/metamodel';
import {MapFeatureTypes} from '../../../pages/client/monitor/map/map-feature.types';
import {RouteService} from '../route/route.service';
import {Icon, Style} from 'ol/style.js';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {TaskUtils} from '../../../pages/client/tasks/tasks-manager/utils/task-utils';
import {
  ProTasksFormRouteUtils
} from '../../../pages/client/tasks/pro-tasks-manager/form/map/utils/pro-tasks-form-route-utils';
import {SubscriptionUtils} from '../../commons/subscription.utils';

@Component({
  selector: 'app-polyline-interactions',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PolylineInteractionsComponent implements OnInit, OnDestroy, OnChanges {

  @Input()
  private map: OlMap;

  @Input()
  private polylineLayer: any;

  @Input()
  private waypointMapType: string

  @Input()
  private polylineMapType: string;

  @Input()
  private originMapType: string;

  @Input()
  private destinationMapTye: string;

  @Input()
  private activeRoute: string;

  @Input()
  private activeRouteRequest: any;

  @Input()
  private activeRoutePolyline: string;

  @Output()
  private recalculateRoute: EventEmitter<void> = new EventEmitter<void>();

  private subSubject = new Subject<boolean>();

  private wayPointIndicator: Feature;
  private olDragListener: Translate = new Translate();
  private dragSubject: Subject<any> = new Subject();
  private pointerMoveEvent: any;
  private pointerDragEvent: any;
  private dblClickEvent: any;

  constructor(private routeService: RouteService) {
  }

  public ngOnInit(): void {
    this.dragSubject.pipe(
        debounceTime(40),
        distinctUntilChanged(),
        takeUntil(this.subSubject))
      .subscribe(
      data => {
        this.callHereRoute(data.event.coordinate);
      }
    );
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['map'] && this.map != null) {
      this.unsubscribeMapEvents();
      this.initPointerMove();
      this.initDblClick();
      this.initPointerDrag();
    }
  }

  private callHereRoute(coordinates: any) {
    let activePolyline = this.polylineLayer.getSource().getFeatures()
      .filter(it => it.values_.name === this.activeRoute && it.values_.type === this.polylineMapType)[0];
    let routeCoords = activePolyline.getGeometry().getCoordinates();
    let origin = this.getMapPosition(routeCoords[0])
    let destination = this.getMapPosition(routeCoords[routeCoords.length - 1]);
    let sortedWaypoints = this.concatAndSortWaypoints(coordinates);
    let routeRequest = this.formSimpleRouteRequest(origin, destination, sortedWaypoints);
    this.routeService.simpleHereRoute(routeRequest, +this.map.getView().getZoom().toFixed(0))
      .pipe(takeUntil(this.subSubject))
      .subscribe(
      data => {
        if (data.polyline !== '') {
          let route = MapFeatureUtils.getTaskGeometry(data.polyline);
          activePolyline.setGeometry(route)
        }
      });
  }

  private getMapPosition(point: any) {
    let lngLat = transform(point, 'EPSG:3857', 'EPSG:4326');
    return {
      address: '',
      lat: +lngLat[1].toFixed(6),
      lng: +lngLat[0].toFixed(6)
    }
  }

  private formSimpleRouteRequest(origin: MapPosition, destination: MapPosition, wayPoints: MapPosition[]) {
    return {
      origin: origin,
      destination: destination,
      waypoints: wayPoints,
      strategy: 'FASTEST',
      vehicleType: 'TRUCK',
    }
  }

  private initPointerMove() {
    this.pointerMoveEvent = this.map.on('pointermove', (event) => {
      let feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        return feature;
      });
      if (feature && feature.values_.type === this.polylineMapType && feature.values_.name === this.activeRoute) {
        if (this.wayPointIndicator != null) {
          this.wayPointIndicator.getGeometry().setCoordinates(event.coordinate);
        } else {
          this.wayPointIndicator = MapFeatureUtils
            .getPointFeature(event, MapFeatureTypes.TASK_WAYPOINT_INDICATOR_MARKER);
          this.wayPointIndicator.setStyle(new Style({
            zIndex: 3,
            image: new Icon(({
              crossOrigin: 'anonymous',
              src: 'assets/img/proTasks/markers/directionsIndicator/DOT.svg',
            }))
          }));
          this.polylineLayer.getSource().addFeature(this.wayPointIndicator);
        }
        this.addDragInteraction(feature, this.wayPointIndicator, true)
      } else if (!feature && !event.dragging && this.wayPointIndicator != null) {
        this.removeFeatureFromLayer(this.wayPointIndicator, this.polylineLayer);
        this.wayPointIndicator = null;
      }
    });
  }

  private initDblClick() {
    this.dblClickEvent = this.map.on('dblclick', (event) => {
      let feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        return feature;
      });
      if (feature && feature.values_.type === this.waypointMapType && feature.values_.name === this.activeRoute) {
        this.removeWaypoint(feature, true)
      }
    });
  }

  private initPointerDrag() {
    this.pointerDragEvent = this.map.on('pointerdrag', (event) => {
      let feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        return feature;
      });
      if (feature && feature.values_.type === this.waypointMapType && feature.values_.name === this.activeRoute) {
        this.addDragInteraction(feature, feature, true, true)
      } else if (feature
        && (feature.values_.type === this.originMapType
          || feature.values_.type === this.destinationMapTye)
        && feature.values_.name === this.activeRoute) {
        this.addDragInteraction(feature, feature, false);
      }
    });
  }

  private addDragInteraction(feature: Feature, featureCollection: any, draggingWaypoint?: boolean, needClearing?: boolean) {
    this.olDragListener = new Translate({
      features: new Collection([featureCollection])
    });
    this.map.addInteraction(this.olDragListener);
    if (draggingWaypoint) {
      this.waypointDrag(feature, needClearing);
    } else {
      this.markerDrag(feature);
    }
  }

  private waypointDrag(feature: Feature, needClearing: boolean) {
    this.olDragListener.on('translatestart', (event) => {
      if (needClearing) {
        this.removeWaypoint(feature, false);
      }
    });
    this.olDragListener.on('translating', (event) => {
      this.dragSubject.next({event, feature});
    });
    this.olDragListener.on('translateend', (event) => {
      event.preventDefault();
      this.activeRouteRequest.waypoints = this.concatAndSortWaypoints(event.coordinate);
      this.recalculateRoute.next();
      this.removeDragInteraction();
    });
  }

  private markerDrag(feature: Feature) {
    this.olDragListener.on('translateend', (event) => {
      event.preventDefault();
      let lonLat = transform(feature.getGeometry().getCoordinates(), 'EPSG:3857', 'EPSG:4326');
      if (feature.values_.type === this.originMapType) {
        this.activeRouteRequest.origin = {
          lat: +lonLat[1].toFixed(6),
          lng: +lonLat[0].toFixed(6)
        };
      } else {
        this.activeRouteRequest.destination = {
          lat: +lonLat[1].toFixed(6),
          lng: +lonLat[0].toFixed(6)
        };
      }
      this.recalculateRoute.next();
      this.removeDragInteraction();
    });
  }

  private concatAndSortWaypoints(coordinates: any) {
    let waypointLonlat = transform(coordinates, 'EPSG:3857', 'EPSG:4326');
    let waypoints = [];
    if (this.activeRouteRequest.waypoints) {
      waypoints = waypoints.concat(this.activeRouteRequest.waypoints)
    }
    waypoints.push(TaskUtils.toOlMapPosition(waypointLonlat));
    return ProTasksFormRouteUtils.sortWaypoints(this.activeRoutePolyline, waypoints);
  }

  private removeWaypoint(feature: Feature, reloadRoute: boolean) {
    let latLng = transform(feature.getGeometry().flatCoordinates, 'EPSG:3857', 'EPSG:4326');
    let waypoint = this.activeRouteRequest.waypoints
      .find(it => it.lat.toFixed(6) === latLng[1].toFixed(6)
        && it.lng.toFixed(6) === latLng[0].toFixed(6));
    let idx = this.activeRouteRequest.waypoints.indexOf(waypoint);
    this.activeRouteRequest.waypoints.splice(idx, 1);
    if (reloadRoute) {
      this.removeFeatureFromLayer(feature, this.polylineLayer)
      this.recalculateRoute.next();
    }
  }

  private removeDragInteraction() {
    this.map.removeInteraction(this.olDragListener);
    this.olDragListener = null;
    if (this.wayPointIndicator != null) {
      this.removeFeatureFromLayer(this.wayPointIndicator, this.polylineLayer);
      this.wayPointIndicator = null;
    }
  }

  private removeFeatureFromLayer(feature: Feature, layer: VectorLayer) {
    try {
      if (feature != null && layer.getSource().hasFeature(feature)) {
        layer.getSource().removeFeature(feature)
      }
    } catch (e) {}
  }

  private unsubscribeMapEvents() {
    if (this.map != null ) {
      this.map.un('pointermove', this.pointerMoveEvent);
      this.map.un('pointerdrag', this.pointerDragEvent);
      this.map.un('dblclick', this.dblClickEvent);
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribeMapEvents();
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
  }

}
